import { Component, inject, Input, OnChanges, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { ModalService } from 'app/services/modal.service';
import * as filterHelpers from '../../helpers/filter.helpers';
import { ModeNamesEnum } from '../../models/browse-mode';
import { BrowseTypesEnum } from '../../models/browse-types.enum';
import { BrowseFilter, FilterNamesEnum } from '../../models/filter';
import { FilterOption } from '../../models/filter-options';
import {
  BrowseSorting,
  SortingValuesEnum,
  getSortings,
} from '../../models/sorting';
import { Sport } from '../../models/sport';
import { FilterStore } from '../../store/filter/filter.store';
import { SortingStore } from '../../store/sorting/sorting.store';
import { SportStore } from '../../store/sport/sport.store';
import { AllFiltersComponent } from '../all-filters/all-filters.component';
import { TranslateModule } from '@ngx-translate/core';
import {
  NgbDropdown,
  NgbDropdownToggle,
  NgbDropdownMenu,
  NgbDropdownItem,
} from '@ng-bootstrap/ng-bootstrap';
import { FilterBoxComponent } from './filter-box/filter-box.component';
import { HorizontalScrollerComponent } from '../horizontal-scroller/horizontal-scroller.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { IconComponent } from '@kinomap-dev/ng-ui-lib';
import { Store } from '@ngrx/store';
import { selectMeUnit } from 'app/store/me/me.selectors';
import { configureFiltersModal } from 'app/helpers/modal-helpers';

@Component({
  selector: 'app-filter-sort-bar',
  templateUrl: './filter-sort-bar.component.html',
  styleUrls: ['./filter-sort-bar.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    HorizontalScrollerComponent,
    FilterBoxComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    AsyncPipe,
    TranslateModule,
    IconComponent,
  ],
})
export class FilterSortBarComponent implements OnChanges, OnDestroy {
  @Input() isSearch: boolean = false;
  @Input() modeName: ModeNamesEnum | null = null;

  private modalService: ModalService = inject(ModalService);
  private filterStore: FilterStore = inject(FilterStore);
  private sortingStore: SortingStore = inject(SortingStore);
  private sportStore: SportStore = inject(SportStore);
  private filterSortingSubscriptions: Subscription[] = [];
  private modalRenderedSubscription: Subscription | undefined = undefined;
  private store: Store = inject(Store);

  filters: BrowseFilter[] = [];
  sortings: BrowseSorting[] = getSortings();
  availableSortings: BrowseSorting[] = [];
  selectedSorting: BrowseSorting = this.sortings[0];
  selectedFilter: BrowseFilter | null = null;
  selectedSport$: Observable<Sport> = this.sportStore.selectedSport$;
  maxFiltersDisplayed: number = 6;
  sortDirection: 'asc' | 'desc' = 'desc';
  sortDirectionFilter: FilterNamesEnum = FilterNamesEnum.SortDirection;
  unit: Observable<string> = this.store.select(selectMeUnit);

  get displayedFilters(): BrowseFilter[] {
    const filters: BrowseFilter[] =
      this.filters.length <= this.maxFiltersDisplayed
        ? [...this.filters]
        : [...this.filters].splice(0, this.maxFiltersDisplayed);
    return filters.filter(
      (f: BrowseFilter) => f.name !== FilterNamesEnum.SortDirection
    );
  }

  get numberOfActiveFilters(): number {
    return (
      this.filters.filter((f: BrowseFilter) => filterHelpers.isFilterActive(f))
        .length - 1
    );
  }

  ngOnChanges(): void {
    this.filterSortingSubscriptions.forEach((sub: Subscription) =>
      sub.unsubscribe()
    );

    this.filterSortingSubscriptions = [];

    if (this.isSearch) {
      this.filterSortingSubscriptions.push(
        this.subscriptionToFiltersToChangeSortingsInSearch(),
        ...this.subscriptionsToFiltersAndSorting('searchedContent')
      );
      return;
    }

    switch (this.modeName) {
      case ModeNamesEnum.Videos:
        this.filterSortingSubscriptions.push(
          ...this.subscriptionsToFiltersAndSorting('video')
        );
        break;
      case ModeNamesEnum.Gps:
        this.filterSortingSubscriptions.push(
          ...this.subscriptionsToFiltersAndSorting('gps')
        );
        break;
      case ModeNamesEnum.Workouts:
        this.filterSortingSubscriptions.push(
          ...this.subscriptionsToFiltersAndSorting('workout'),
          this.sportStore.selectedSport$.subscribe(() => {
            this.filterStore.resetWorkoutTypeSelectedOptions();
          })
        );
        break;
      case ModeNamesEnum.Coachings:
        this.filterSortingSubscriptions.push(
          ...this.subscriptionsToFiltersAndSorting('coaching')
        );
        break;
    }

    this.setAvailableSortingsInCatalog();
  }

  ngOnDestroy(): void {
    this.filterSortingSubscriptions.forEach((sub: Subscription) =>
      sub.unsubscribe()
    );
    this.modalRenderedSubscription?.unsubscribe();
  }

  onDisplayAllFilters(): void {
    this.selectedFilter = null;
    this.modalService.display();
    this.modalRenderedSubscription?.unsubscribe();
    this.modalService.setDialogComponent(AllFiltersComponent);
    this.modalService.reset();
    this.modalRenderedSubscription?.unsubscribe();
    this.modalService.setClassName('bank-card-form-modal');
    this.modalService.display();
    setTimeout(
      () =>
        (this.modalRenderedSubscription = configureFiltersModal(
          this.modalService,
          this.isSearch,
          this.modeName
        ))
    );
  }

  getSelectedSortingLabel(label: string): string {
    return label.split('(')?.[0];
  }

  toggleSortDirection(): void {
    const filter: BrowseFilter = this.filters.find(
      (f: BrowseFilter) => f.name === FilterNamesEnum.SortDirection
    );

    if (!filter) return;

    const current: string = filter.selectedOptions[0]?.value || 'asc';
    const next: 'asc' | 'desc' = current === 'asc' ? 'desc' : 'asc';
    filter.selectedOptions = [
      { value: next, label: next === 'asc' ? 'Ascending' : 'Descending' },
    ];

    this.onApplyFilter(FilterNamesEnum.SortDirection, filter.selectedOptions);
    this.sortDirection = next;
  }

  onApplyFilter(
    filterName: FilterNamesEnum,
    filterOptions: FilterOption[]
  ): void {
    const filter: BrowseFilter = this.filters.find((filter: BrowseFilter) => {
      return filter.name === filterName;
    });

    if (!filter) {
      return;
    }

    filter.selectedOptions = filterOptions;
    this.onSelectOrUnselectFilter(filterName);

    if (this.isSearch) {
      this.filterStore.setSearchedContentFilters(this.filters);
      return;
    }

    switch (this.modeName) {
      case ModeNamesEnum.Videos:
        return this.filterStore.setVideoFilters(this.filters);
      case ModeNamesEnum.Gps:
        return this.filterStore.setGpsFilters(this.filters);
      case ModeNamesEnum.Workouts:
        return this.filterStore.setWorkoutFilters(this.filters);
      case ModeNamesEnum.Coachings:
        return this.filterStore.setCoachingFilters(this.filters);
    }
  }

  onSelectOrUnselectFilter(filterName: FilterNamesEnum): void {
    if (this.selectedFilter && this.selectedFilter.name === filterName) {
      return (this.selectedFilter = null);
    }
    this.selectedFilter = this.filters.find((filter: BrowseFilter) => {
      return filter.name === filterName;
    });
  }

  isFilterActive(filter: BrowseFilter): boolean {
    return filterHelpers.isFilterActive(filter);
  }

  areExtraFiltersActive(): boolean {
    if (this.filters.length <= this.maxFiltersDisplayed) {
      return false;
    }

    const extraFilters: BrowseFilter[] = this.filters.filter(
      (f: BrowseFilter) =>
        !this.displayedFilters.includes(f) &&
        f.name !== FilterNamesEnum.SortDirection
    );
    return extraFilters.some((f: BrowseFilter) => this.isFilterActive(f));
  }

  onChangeSorting(sortingValue: SortingValuesEnum): void {
    const sorting: BrowseSorting = this.sortings.find(
      (sorting: BrowseSorting) => sorting.value === sortingValue
    );

    if (!sorting || this.selectedSorting?.value === sorting?.value) {
      return;
    }

    if (this.isSearch) {
      this.sortingStore.setSearchedContentSorting(sorting);
      return;
    }

    switch (this.modeName) {
      case ModeNamesEnum.Videos:
        return this.sortingStore.setVideoSorting(sorting);
      case ModeNamesEnum.Gps:
        return this.sortingStore.setGpsSorting(sorting);
      case ModeNamesEnum.Workouts:
        return this.sortingStore.setWorkoutSorting(sorting);
      case ModeNamesEnum.Coachings:
        return this.sortingStore.setCoachingSorting(sorting);
    }
  }

  private subscriptionsToFiltersAndSorting(
    contentType: 'gps' | 'workout' | 'coaching' | 'video' | 'searchedContent'
  ): Subscription[] {
    const obj = {
      video: {
        filters: this.filterStore.videoFilters$,
        sorting: this.sortingStore.videoSorting$,
      },
      gps: {
        filters: this.filterStore.gpsFilters$,
        sorting: this.sortingStore.gpsSorting$,
      },
      workout: {
        filters: this.filterStore.workoutFilters$,
        sorting: this.sortingStore.workoutSorting$,
      },
      coaching: {
        filters: this.filterStore.coachingFilters$,
        sorting: this.sortingStore.coachingSorting$,
      },
      searchedContent: {
        filters: this.filterStore.searchedContentFilters$,
        sorting: this.sortingStore.searchedContentSorting$,
      },
    };

    return [
      obj[contentType].filters.subscribe((filters: BrowseFilter[]) => {
        this.filters = [...filters].map((f: BrowseFilter) => {
          return { ...f };
        });
      }),
      obj[contentType].sorting.subscribe((sorting: BrowseSorting) => {
        this.selectedSorting = { ...sorting };
      }),
    ];
  }

  private subscriptionToFiltersToChangeSortingsInSearch(): Subscription {
    return this.filterStore.searchedContentFilters$.subscribe(
      (filters: BrowseFilter[]) => {
        const modeFilter: BrowseFilter = filters.find(
          (f: BrowseFilter) => f.name === FilterNamesEnum.SearchMode
        );
        const searchMode: string = modeFilter.selectedOptions[0].value;

        this.setAvailableSortingsInSearch(searchMode);
      }
    );
  }

  private setAvailableSortingsInCatalog(): void {
    const availableSortingValues: SortingValuesEnum[] = [
      SortingValuesEnum.Recent,
      SortingValuesEnum.Popular,
    ];
    switch (this.modeName) {
      case ModeNamesEnum.Videos:
        availableSortingValues.push(
          SortingValuesEnum.Distance,
          SortingValuesEnum.Difficulty,
          SortingValuesEnum.Duration,
          SortingValuesEnum.Slope
        );
        break;
      case ModeNamesEnum.Gps:
        availableSortingValues.push(
          SortingValuesEnum.Distance,
          SortingValuesEnum.Slope
        );
        break;
      case ModeNamesEnum.Workouts:
        availableSortingValues.push(
          SortingValuesEnum.Duration,
          SortingValuesEnum.Difficulty
        );
        break;
      case ModeNamesEnum.Coachings:
        availableSortingValues.push(
          SortingValuesEnum.Duration,
          SortingValuesEnum.Difficulty
        );
        break;
    }

    this.availableSortings = this.sortings.filter((s: BrowseSorting) => {
      return availableSortingValues.includes(s.value);
    });
  }

  private setAvailableSortingsInSearch(searchMode: string): void {
    const availableSortingValues: SortingValuesEnum[] = [
      SortingValuesEnum.Recent,
      SortingValuesEnum.Popular,
    ];

    switch (searchMode) {
      case BrowseTypesEnum.Video:
        availableSortingValues.push(
          SortingValuesEnum.Distance,
          SortingValuesEnum.Difficulty,
          SortingValuesEnum.Duration,
          SortingValuesEnum.Slope
        );
        break;
      case BrowseTypesEnum.Coaching:
        availableSortingValues.push(
          SortingValuesEnum.Duration,
          SortingValuesEnum.Difficulty
        );
        break;
    }

    this.availableSortings = this.sortings.filter((s: BrowseSorting) => {
      return availableSortingValues.includes(s.value);
    });
  }
}
